// PostThumbnail: thumbnail for a writing card.
//
// Tries the provided `src` first. If that fails (or none was provided), renders
// a branded SVG fallback with the tag and outlet name on a tag-coloured gradient.
// This guarantees every post card has a visual — no empty boxes, no design holes.

import { useState } from 'react';
import { cx } from '@lib/utils/classNames';

export interface PostThumbnailProps {
  /** Real image URL. If omitted (or fails to load), a generated fallback is used. */
  src?: string;
  /** Post tag — drives the fallback colour. */
  tag: string;
  /** Post title — read by screen readers. */
  title: string;
  /** Optional outlet name shown small in the fallback corner. */
  outlet?: string;
  className?: string;
}

/** Tag → [start, end, accent] colour stops for the generated gradient. */
const TAG_PALETTE: Record<string, readonly [string, string, string]> = {
  'Op-ed':     ['#1A2E5E', '#0B1E20', '#5BEFD0'],
  Essay:       ['#3D1F4E', '#0B1E20', '#FF85A3'],
  Analysis:    ['#0B2638', '#0B1E20', '#14C7C3'],
  Tutorial:    ['#0B3326', '#0B1E20', '#57D092'],
  Interview:   ['#4E1F2E', '#0B1E20', '#FF5079'],
  Research:    ['#1E2A56', '#0B1E20', '#93F7E4'],
  Post:        ['#112A2D', '#050D0E', '#5BEFD0'],
};

function getPalette(tag: string): readonly [string, string, string] {
  return TAG_PALETTE[tag] ?? TAG_PALETTE['Post']!;
}

/** Detect the outlet from a URL hostname. Used for both label + future logo work. */
function detectOutlet(href: string | undefined): string | undefined {
  if (!href) return undefined;
  try {
    const host = new URL(href).hostname.toLowerCase().replace(/^www\./, '');
    if (host.includes('blockworks.co'))         return 'Blockworks';
    if (host.includes('hackernoon.com'))        return 'HackerNoon';
    if (host.includes('dev.to'))                return 'Dev.to';
    if (host.includes('hashnode'))              return 'Hashnode';
    if (host.includes('blog.alisonhaire.com'))  return 'alisonhaire.com';
    if (host.includes('blog.lilypad.tech'))     return 'Lilypad';
    if (host.includes('crowdfundinsider.com'))  return 'Crowdfund Insider';
    if (host.includes('siliconangle.com'))      return 'SiliconANGLE';
    return host;
  } catch {
    return undefined;
  }
}

/** Render the SVG fallback poster. Pure presentation; no fetching. */
function FallbackPoster({
  tag,
  title,
  outlet,
}: {
  tag: string;
  title: string;
  outlet: string;
}) {
  const [start, end, accent] = getPalette(tag);
  // Pick a stable seed from the title so different posts get slightly different motifs.
  const seed = Array.from(title).reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) >>> 0, 0);
  const ringOffsetX = 30 + (seed % 40);
  const ringOffsetY = 20 + ((seed >> 4) % 30);
  const ringSize = 60 + ((seed >> 8) % 30);

  return (
    <svg
      viewBox="0 0 600 338"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={`${tag} · ${title}`}
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      <defs>
        <linearGradient id={`pt-bg-${seed}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={start} />
          <stop offset="100%" stopColor={end} />
        </linearGradient>
        <radialGradient id={`pt-glow-${seed}`} cx="0.5" cy="0.5">
          <stop offset="0%" stopColor={accent} stopOpacity="0.45" />
          <stop offset="60%" stopColor={accent} stopOpacity="0.10" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Background */}
      <rect width="600" height="338" fill={`url(#pt-bg-${seed})`} />
      {/* Glow blob */}
      <circle
        cx={600 - ringOffsetX * 2}
        cy={ringOffsetY * 2}
        r={ringSize * 2.5}
        fill={`url(#pt-glow-${seed})`}
      />
      {/* Concentric rings */}
      {[1, 2, 3].map((i) => (
        <circle
          key={i}
          cx={600 - ringOffsetX * 2}
          cy={ringOffsetY * 2}
          r={ringSize * i}
          fill="none"
          stroke={accent}
          strokeOpacity={0.15 / i}
          strokeWidth={1}
        />
      ))}
      {/* Hairline */}
      <line x1="0" y1="337.5" x2="600" y2="337.5" stroke={accent} strokeOpacity="0.18" />
      {/* Tag label, top-left */}
      <text
        x="32"
        y="50"
        fill={accent}
        fontFamily="'Space Mono', ui-monospace, monospace"
        fontSize="13"
        letterSpacing="2.6"
        style={{ textTransform: 'uppercase' }}
      >
        {tag.toUpperCase()}
      </text>
      {/* Outlet label, bottom-left */}
      <text
        x="32"
        y="310"
        fill="#B6D0CC"
        fontFamily="'Space Mono', ui-monospace, monospace"
        fontSize="11"
        letterSpacing="1.4"
        opacity="0.7"
      >
        {outlet.toUpperCase()}
      </text>
    </svg>
  );
}

export function PostThumbnail({
  src,
  tag,
  title,
  outlet,
  className,
}: PostThumbnailProps) {
  const [errored, setErrored] = useState(false);
  const resolvedOutlet = outlet ?? 'WRITING';
  const showFallback = !src || errored;

  return (
    <div className={cx('post-thumb', className)}>
      {showFallback ? (
        <FallbackPoster tag={tag} title={title} outlet={resolvedOutlet} />
      ) : (
        <img
          src={src}
          alt={`${tag} · ${title}`}
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </div>
  );
}

// Re-export the URL → outlet helper for callers that want a consistent outlet label.
export { detectOutlet };
